/**
 * Centralized category mapping utilities for WillPower Store
 * Provides consistent mapping between Shopify product types and frontend category slugs
 */

/**
 * Master category mapping - single source of truth
 * Maps Shopify product types to frontend category slugs
 */
export const CATEGORY_MAPPING: Record<string, string> = {
  // Development categories
  Protein: "protein",
  "Pre-Workout": "pre-workout",
  Vitamins: "vitamins",
  Creatine: "creatine",
  "Fat Burners": "fat-burners",
  Recovery: "recovery",
  Performance: "performance",
  Supplements: "supplements",

  // Production categories (from screenshot)
  "Bcaa & Eaa": "bcaa-eaa",
  "Creatine & Pre Workout": "creatine-pre-workout",
  Mass: "mass",
  "Vegan Protein": "vegan-protein",
};

/**
 * Reverse mapping - frontend category slugs to Shopify product types
 * Generated automatically from the master mapping
 */
export const REVERSE_CATEGORY_MAPPING: Record<string, string> =
  Object.fromEntries(
    Object.entries(CATEGORY_MAPPING).map(([productType, slug]) => [
      slug,
      productType,
    ])
  );

/**
 * Transform Shopify product type to frontend category slug
 */
export function mapProductTypeToCategory(productType: string): string {
  return (
    CATEGORY_MAPPING[productType] ||
    productType.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")
  );
}

/**
 * Transform frontend category slug to Shopify product type
 */
export function mapCategoryToProductType(
  categorySlug: string
): string | undefined {
  return REVERSE_CATEGORY_MAPPING[categorySlug];
}

/**
 * Get display name for a category slug
 */
export function getCategoryDisplayName(categorySlug: string): string {
  // Find the product type for this slug
  const productType = REVERSE_CATEGORY_MAPPING[categorySlug];
  if (productType) {
    return productType;
  }

  // Fallback: convert slug to title case
  return categorySlug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Check if a category slug is valid (has a mapping)
 */
export function isValidCategorySlug(categorySlug: string): boolean {
  return categorySlug in REVERSE_CATEGORY_MAPPING;
}

/**
 * Check if a product type is valid (has a mapping)
 */
export function isValidProductType(productType: string): boolean {
  return productType in CATEGORY_MAPPING;
}

/**
 * Get all available category slugs
 */
export function getAllCategorySlugs(): string[] {
  return Object.values(CATEGORY_MAPPING);
}

/**
 * Get all available product types
 */
export function getAllProductTypes(): string[] {
  return Object.keys(CATEGORY_MAPPING);
}

/**
 * Master brand mapping - single source of truth
 * Maps Shopify vendors to frontend brand slugs
 */
export const BRAND_MAPPING: Record<string, string> = {
  "Optimum Nutrition": "optimum-nutrition",
  Myprotein: "myprotein",
  Grenade: "grenade",
  Bulk: "bulk",
  USN: "usn",
  "Applied Nutrition": "applied-nutrition",
};

/**
 * Transform Shopify vendor to frontend brand slug
 */
export function mapVendorToBrand(vendor: string): string {
  return BRAND_MAPPING[vendor] || vendor.toLowerCase().replace(/\s+/g, "-");
}
